/**
 * Componente ResumenCompra
 * Muestra el resumen completo antes de guardar
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
} from '@mui/material';

const ResumenCompra = ({ compraData = {}, items = [], retenciones = [] }) => {
  const subtotal = items.reduce((sum, item) => sum + parseFloat(item.subtotal || 0), 0);
  const iva = subtotal * 0.15;
  const total = subtotal + iva;
  const totalRetenciones = retenciones.reduce((sum, r) => sum + parseFloat(r.valor || 0), 0);
  const totalAPagar = total - totalRetenciones;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Paso 6: Resumen de la Compra
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Revise todos los datos antes de guardar la compra.
      </Alert>

      {/* Datos Básicos */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Datos de la Factura
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">N° Factura</Typography>
            <Typography>{compraData.numero_factura || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Fecha Factura</Typography>
            <Typography>{compraData.fecha_factura || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Código Autorización</Typography>
            <Typography>{compraData.codigo_autorizacion || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Fecha Ingreso</Typography>
            <Typography>{compraData.fecha_ingreso || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Proveedor</Typography>
            <Typography>{compraData.proveedor_nombre || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">RUC Proveedor</Typography>
            <Typography>{compraData.proveedor_ruc || '-'}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Items */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Items ({items.length})
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="right">Cantidad</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="right">Subtotal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.codigo || '-'}</TableCell>
                  <TableCell>{item.descripcion || '-'}</TableCell>
                  <TableCell align="right">{item.cantidad}</TableCell>
                  <TableCell align="right">${parseFloat(item.precio_unitario || 0).toFixed(2)}</TableCell>
                  <TableCell align="right">${parseFloat(item.subtotal || 0).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Cuentas Contables */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Cuentas Contables
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Cuenta Principal</Typography>
            <Typography>{compraData.cuenta_principal_nombre || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Cuenta Proveedores</Typography>
            <Typography>{compraData.cuenta_proveedores_id || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">Cuenta IVA Compras</Typography>
            <Typography>{compraData.cuenta_iva_compras_id || '-'}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Retenciones */}
      {compraData.aplicar_retenciones && retenciones.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Retenciones
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Porcentaje</TableCell>
                  <TableCell align="right">Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {retenciones.map((ret, index) => (
                  <TableRow key={index}>
                    <TableCell>{ret.codigo}</TableCell>
                    <TableCell>{ret.tipo}</TableCell>
                    <TableCell align="right">{ret.porcentaje}%</TableCell>
                    <TableCell align="right">${parseFloat(ret.valor).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Nota */}
      {compraData.nota && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Nota
          </Typography>
          <Typography>{compraData.nota}</Typography>
        </Paper>
      )}

      {/* Totales */}
      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Totales
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <Grid container spacing={1}>
          <Grid item xs={8}>
            <Typography>Subtotal:</Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'right' }}>
            <Typography>${subtotal.toFixed(2)}</Typography>
          </Grid>
          <Grid item xs={8}>
            <Typography>IVA (15%):</Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'right' }}>
            <Typography>${iva.toFixed(2)}</Typography>
          </Grid>
          <Grid item xs={8}>
            <Typography fontWeight="bold">Total:</Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'right' }}>
            <Typography fontWeight="bold">${total.toFixed(2)}</Typography>
          </Grid>
          {totalRetenciones > 0 && (
            <>
              <Grid item xs={8}>
                <Typography color="error">Retenciones:</Typography>
              </Grid>
              <Grid item xs={4} sx={{ textAlign: 'right' }}>
                <Typography color="error">-${totalRetenciones.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography fontWeight="bold" color="primary">Total a Pagar:</Typography>
              </Grid>
              <Grid item xs={4} sx={{ textAlign: 'right' }}>
                <Typography fontWeight="bold" color="primary">
                  ${totalAPagar.toFixed(2)}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default ResumenCompra;
